import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useEngine } from '../../state/engineStore';
import type { EngineEvent } from '../../engine/core/events';
import { fmtSimTime } from '../shell/format';
import { PRI_COLOUR } from '../alarms/AlarmBanner';

const KIND_LABEL: Record<string, string> = {
  alarm: 'Alarm',
  trip: 'Trip',
  reset: 'Reset',
  bypass: 'Bypass',
  action: 'Operator',
  scenario: 'Scenario',
  fault: 'Fault',
  engine: 'System',
  sequence: 'Sequence',
  objective: 'Objective',
};

function describe(e: EngineEvent): { text: string; colour?: string } {
  switch (e.k) {
    case 'alarm':
      return { text: `${e.tag} — ${e.text} (${e.transition})`, colour: PRI_COLOUR[e.priority] };
    case 'trip':
      return { text: `${e.text}${e.firstOut ? ` — first out: ${e.firstOut}` : ''}`, colour: 'var(--pri-emergency)' };
    case 'reset':
      return { text: `Reset ${e.text}`, colour: 'var(--ok)' };
    case 'bypass':
      return { text: `${e.on ? 'Bypassed' : 'Bypass removed'} ${e.id} — ${e.reason}`, colour: 'var(--pri-medium)' };
    case 'action':
      return { text: e.text };
    case 'scenario':
      return { text: e.text, colour: 'var(--info)' };
    case 'fault':
      return { text: `${e.text} on ${e.target} ${e.active ? 'INJECTED' : 'cleared'}`, colour: 'var(--utility)' };
    case 'engine':
      return { text: e.text, colour: e.severity === 'error' ? 'var(--pri-emergency)' : 'var(--pri-medium)' };
    case 'sequence':
      return { text: `${e.text} (${e.from} → ${e.to})` };
    case 'objective':
      return { text: `${e.met ? '✓' : '✗'} ${e.text}`, colour: e.met ? 'var(--ok)' : 'var(--pri-high)' };
  }
}

export function EventLog(): JSX.Element {
  const events = useEngine((s) => s.events);
  const [kinds, setKinds] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  const bottom = useRef<HTMLDivElement>(null);
  const [follow, setFollow] = useState(true);

  const rows = useMemo(() => {
    const q = query.trim().toUpperCase();
    return events.filter((e) => {
      if (kinds.size > 0 && !kinds.has(e.k)) return false;
      if (!q) return true;
      return describe(e).text.toUpperCase().includes(q) || e.k.toUpperCase().includes(q);
    });
  }, [events, kinds, query]);

  useEffect(() => {
    if (follow) bottom.current?.scrollIntoView({ block: 'end' });
  }, [rows.length, follow]);

  const exportCsv = (): void => {
    const lines = ['simTime_s,kind,description'];
    for (const e of rows) {
      lines.push(`${(e.step * 0.1).toFixed(1)},${e.k},"${describe(e).text.replace(/"/g, '""')}"`);
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'setpoint-events.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '7px 10px', borderBottom: '1px solid var(--line-dim)', flexWrap: 'wrap' }}>
        {Object.keys(KIND_LABEL).map((k) => (
          <button
            key={k}
            className={kinds.has(k) ? 'on' : ''}
            style={{ padding: '2px 7px', fontSize: 11.5 }}
            onClick={() =>
              setKinds((s) => {
                const n = new Set(s);
                if (n.has(k)) n.delete(k);
                else n.add(k);
                return n;
              })
            }
          >
            {KIND_LABEL[k]}
          </button>
        ))}
        <input placeholder="Filter…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ width: 180 }} />
        <span style={{ flex: 1 }} />
        <button className={follow ? 'on' : ''} onClick={() => setFollow(!follow)}>
          Follow
        </button>
        <button onClick={exportCsv}>Export CSV</button>
      </div>
      <div className="scroll" style={{ flex: 1, padding: '4px 0' }}>
        {rows.map((e, i) => {
          const d = describe(e);
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                gap: 10,
                padding: '2px 10px',
                fontSize: 12,
                borderLeft: `3px solid ${d.colour ?? 'transparent'}`,
              }}
            >
              <span className="num muted" style={{ width: 70, flexShrink: 0 }}>
                {fmtSimTime(e.step * 0.1)}
              </span>
              <span className="muted" style={{ width: 66, flexShrink: 0, fontSize: 11 }}>
                {KIND_LABEL[e.k]}
              </span>
              <span style={{ color: d.colour }}>{d.text}</span>
            </div>
          );
        })}
        <div ref={bottom} />
      </div>
    </div>
  );
}
